import { BaseStore } from "@infrastructure/stores/_base.store";
import { ServiceType } from "@domain/enums/machine.enums";
import { ServiceMetadata } from "@infrastructure/clients/http/clients/service.schemas";

export interface ColorOption {
  id: string;
  title: string;
}

export interface ServiceMetadataItem {
  serviceType: string;
  serviceMode: string;
  name: string;
  description: string;
  recommendations: string | null;
  priceLabel: string;
  hint: string | null;
  iconUrl: string | null;
  displayOrder: number;
  enabled: boolean;
  formatOptions: string[];
  colorOptions: ColorOption[];
  duplexAvailable: boolean;
  offlineAvailable: boolean;
  comingSoon: boolean;
  hasVariants?: boolean;
  isVariant?: boolean;
}

type ServiceMetadataStoreState = {
  services: ServiceMetadataItem[];
  loaded: boolean;
};

class ServiceMetadataStoreFactory extends BaseStore<ServiceMetadataStoreState> {
  private _services: ServiceMetadataItem[] = [];
  private _loaded = false;

  protected createState(): ServiceMetadataStoreState {
    return {
      services: this._services,
      loaded: this._loaded,
    };
  }

  protected getStateKey(): string {
    return `services:${this._services.length}:${this._loaded}`;
  }

  private _mapToItem(s: ServiceMetadata, hasVariants = false, isVariant = false): ServiceMetadataItem {
    return {
      serviceType: s.service_type,
      serviceMode: s.service_mode,
      name: s.name || s.service_type,
      description: s.description || "",
      recommendations: s.recommendations || null,
      priceLabel: s.price_label || "",
      hint: s.hint || null,
      iconUrl: s.icon_url || null,
      displayOrder: s.display_order ?? 0,
      enabled: s.enabled ?? true,
      formatOptions: s.format_options ?? [],
      colorOptions: s.color_options ?? [],
      duplexAvailable: s.duplex_available ?? false,
      offlineAvailable: s.offline_available ?? false,
      comingSoon: s.coming_soon ?? false,
      hasVariants: s.has_variants ?? hasVariants,
      isVariant,
    };
  }

  setServices(services: ServiceMetadata[]): void {
    this._services = services.map((s) => this._mapToItem(s, s.has_variants ?? false, false));
    this._loaded = true;
    this.notifyListeners();
  }

  addVariants(services: ServiceMetadata[]): void {
    const newItems = services.map((s) => this._mapToItem(s, false, true));
    const existingTypes = new Set(this._services.map((x) => x.serviceType));
    for (const item of newItems) {
      if (!existingTypes.has(item.serviceType)) {
        this._services.push(item);
        existingTypes.add(item.serviceType);
      }
    }
    this.notifyListeners();
  }

  getHasVariants(serviceType: string): boolean {
    const item = this._services.find((s) => s.serviceType === serviceType);
    return item?.hasVariants ?? false;
  }

  getByType(serviceType: string): ServiceMetadataItem | undefined {
    return this._services.find((s) => s.serviceType === serviceType);
  }

  getOrderedServices(): ServiceMetadataItem[] {
    return [...this._services].sort((a, b) => a.displayOrder - b.displayOrder);
  }

  getOrderedServicesWithDisabledLast(): ServiceMetadataItem[] {
    return [...this._services].sort((a, b) => {
      if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
      return a.displayOrder - b.displayOrder;
    });
  }

  getOrderedTopLevelServiceTypes(): string[] {
    return this.getOrderedServicesWithDisabledLast()
      .filter((s) => !s.isVariant)
      .map((s) => s.serviceType);
  }
}

export const ServiceMetadataStore = new ServiceMetadataStoreFactory();
